import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Loader2, BookText } from 'lucide-react';
import useTranslate from '../hooks/useTranslate';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Compact MH excerpt finder for a single verse. Pulls the chapter-level
 * commentary, then picks the section whose `verses` range covers the given
 * verse number. If no section explicitly covers it, returns the first
 * section as a fallback (still relevant — section ranges in MHCC are
 * typically wide).
 *
 * Used inside VerseHighlightDialog to surface "what does Matthew Henry
 * say about this verse?" the moment a user picks a highlight color.
 */
const parseRange = (rangeStr) => {
  // "1-8" → [1,8]; "1, 2" → [1,2]; "5" → [5,5]; "1-21, 22-36" → [1,36]
  if (!rangeStr) return [0, 0];
  const nums = String(rangeStr).match(/\d+/g) || [];
  if (nums.length === 0) return [0, 0];
  const ints = nums.map((n) => parseInt(n, 10)).filter((n) => Number.isFinite(n));
  if (ints.length === 0) return [0, 0];
  return [Math.min(...ints), Math.max(...ints)];
};

/**
 * @param {object} props
 * @param {string} props.book      Bible book name, e.g. "John"
 * @param {number} props.chapter   Chapter number
 * @param {number} props.verse     Verse number
 * @param {string} [props.language='en']
 * @param {(excerpt:{verses:string,commentary:string,bookRef:string})=>void} props.onSelect
 *        Called when the user clicks "Save as note" — gives the parent
 *        the section snippet so it can be persisted as a highlight note.
 */
const MatthewHenryVerseExcerpt = ({ book, chapter, verse, language = 'en', onSelect }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!book || !chapter) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    axios
      .get(`${BACKEND_URL}/api/commentary/matthew-henry`, {
        params: { book, chapter },
      })
      .then((r) => {
        if (!cancelled) setData(r.data);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [book, chapter]);

  // Pick the section whose verse range covers `verse` (verse=0 → first section).
  const section = useMemo(() => {
    if (!data?.sections?.length) return null;
    if (!verse) return data.sections[0];
    for (const s of data.sections) {
      const [lo, hi] = parseRange(s.verses);
      if (verse >= lo && verse <= hi) return s;
    }
    return data.sections[0];
  }, [data, verse]);

  const { output: tExcerpt } = useTranslate([section?.commentary || ''], language);

  if (loading) {
    return (
      <div
        className="flex items-center gap-2 text-xs text-amber-800 dark:text-amber-200 py-1"
        data-testid="mh-excerpt-loading"
      >
        <Loader2 className="w-3 h-3 animate-spin" />
        {language === 'es' ? 'Cargando comentario…' : 'Loading commentary…'}
      </div>
    );
  }

  if (error || !section) {
    return null;
  }

  const fullText = tExcerpt[0] || section.commentary;
  const truncated = fullText.length > 380
    ? `${fullText.slice(0, 380).trim()}…`
    : fullText;
  const ref = `${book} ${chapter}:${section.verses}`;

  return (
    <div
      className="rounded-md border border-amber-300/60 dark:border-amber-800/50 bg-amber-50/70 dark:bg-amber-950/30 p-3 space-y-2"
      data-testid="mh-excerpt-card"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 text-xs font-bold text-amber-900 dark:text-amber-100">
          <BookText className="w-3.5 h-3.5" />
          {language === 'es' ? 'Matthew Henry' : 'Matthew Henry'}
        </div>
        <span className="text-[10px] text-amber-800/80 dark:text-amber-200/80 italic">
          {ref}
        </span>
      </div>
      <p className="text-xs leading-relaxed text-slate-800 dark:text-slate-200">
        {truncated}
      </p>
      <button
        type="button"
        onClick={() => onSelect?.({
          verses: section.verses,
          commentary: section.commentary,
          bookRef: ref,
        })}
        className="w-full text-xs font-semibold py-1.5 rounded-md bg-amber-700 hover:bg-amber-800 text-white transition-colors"
        data-testid="mh-excerpt-save-btn"
      >
        {language === 'es'
          ? 'Guardar este comentario como nota'
          : 'Save this commentary as a note'}
      </button>
    </div>
  );
};

export default MatthewHenryVerseExcerpt;
